import React, { useState, useEffect } from 'react';
import '../styles/StreamManagement.css';

const ActiveStreamsManagement = () => {
  const [activeTabs, setActiveTabs] = useState([]);
  const [allStreams, setAllStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedStreamId, setSelectedStreamId] = useState('');
  const [editingTabId, setEditingTabId] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [streamPicker, setStreamPicker] = useState({
    open: false,
    tabId: null,
    currentStreamId: '',
    tabName: '',
  });
  const [pickerSearch, setPickerSearch] = useState('');

  useEffect(() => {
    fetchActiveTabs();
    fetchAllStreams();
  }, []);

  const fetchActiveTabs = async () => {
    try {
      const response = await fetch('/api/streams/tabs');
      const data = await response.json();
      if (response.ok) {
        setActiveTabs(data.streams || []);
      }
    } catch (err) {
      console.error('Error fetching active tabs:', err);
      setError('Failed to fetch active stream tabs');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStreams = async () => {
    try {
      const response = await fetch('/api/streams/all');
      const data = await response.json();
      if (response.ok) {
        setAllStreams(data.streams || []);
      }
    } catch (err) {
      console.error('Error fetching all streams:', err);
      setError('Failed to fetch streams');
    }
  };

  const handleAddActiveStream = async (e) => {
    e.preventDefault();
    if (!selectedStreamId) {
      setError('Please select a stream');
      return;
    }

    try {
      const stream = allStreams.find(s => s.id === parseInt(selectedStreamId));
      if (!stream) {
        setError('Stream not found');
        return;
      }

      const tabName = stream.stream_tab_name || stream.stream_name;

      const response = await fetch(`/api/active-streams/${selectedStreamId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': localStorage.getItem('session_token'),
        },
        body: JSON.stringify({
          tabName: tabName,
          display_order: activeTabs.length,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || data.error || 'Failed to add active stream');
      }

      setSuccessMessage('✅ Stream added to active tabs successfully!');
      setSelectedStreamId('');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchActiveTabs();
    } catch (err) {
      console.error('Error adding stream:', err);
      setError(err.message || 'Error adding stream');
    }
  };

  const handleUpdateDisplayedStream = async (tabId, newStreamId) => {
    try {
      const stream = allStreams.find(s => s.id === parseInt(newStreamId));
      if (!stream) {
        setError('Stream not found');
        return;
      }

      // Use tabId (active_streams row ID) for the update endpoint
      const response = await fetch(`/api/active-streams/${tabId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': localStorage.getItem('session_token'),
        },
        body: JSON.stringify({
          stream_id: newStreamId,
          is_currently_displayed: JSON.stringify({
            stream_id: stream.id,
            stream_type: stream.stream_type,
            stream_name: stream.stream_name,
            stream_video_path: stream.stream_video_path,
            stream_video_link: stream.stream_video_link,
            video_youtube_link: stream.video_youtube_link,
            stream_live_user_id: stream.stream_live_user_id,
          }),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update displayed stream');
      }

      setSuccessMessage('✅ Displayed stream updated!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchActiveTabs();
    } catch (err) {
      console.error('Error updating displayed stream:', err);
      setError(err.message || 'Error updating stream');
    }
  };

  const handleUpdateTabTitle = async (tabId, newTitle) => {
    try {
      const response = await fetch(`/api/active-streams/${tabId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': localStorage.getItem('session_token'),
        },
        body: JSON.stringify({
          stream_tab_name: newTitle,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update tab title');
      }

      setSuccessMessage('✅ Tab title updated!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setEditingTabId(null);
      fetchActiveTabs();
    } catch (err) {
      console.error('Error updating tab title:', err);
      setError(err.message || 'Error updating title');
    }
  };

  const handleToggleActive = async (tabId) => {
    try {
      const tab = activeTabs.find(t => t.active_stream_id === tabId);
      if (!tab) {
        setError('Tab not found');
        return;
      }

      const response = await fetch(`/api/active-streams/${tabId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': localStorage.getItem('session_token'),
        },
        body: JSON.stringify({ is_active: tab.is_active === 1 ? 0 : 1 }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || data.error || 'Failed to toggle active status');
      }

      setSuccessMessage('✅ Stream status updated!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchActiveTabs();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError(err.message || 'Error updating status');
    }
  };

  const handleDeleteActiveStream = async (tabId) => {
    if (!window.confirm('Are you sure you want to remove this stream from active tabs?')) {
      return;
    }

    try {
      const tab = activeTabs.find(t => t.active_stream_id === tabId);
      if (!tab) {
        setError('Tab not found');
        return;
      }

      const response = await fetch(`/api/active-streams/${tabId}`, {
        method: 'DELETE',
        headers: {
          'X-Session-Token': localStorage.getItem('session_token'),
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || data.error || 'Failed to delete stream');
      }

      setSuccessMessage('✅ Stream removed from active tabs!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchActiveTabs();
    } catch (err) {
      console.error('Error deleting stream:', err);
      setError(err.message || 'Error deleting stream');
    }
  };

  const openStreamPicker = (tab) => {
    let currentDisplayedId = '';
    try {
      const v = tab.is_currently_displayed;
      if (typeof v === 'string') {
        const t = v.trim();
        if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
          const obj = JSON.parse(t);
          currentDisplayedId = obj.stream_id || '';
        }
      } else if (typeof v === 'object' && v !== null) {
        currentDisplayedId = v.stream_id || '';
      }
    } catch (e) {
      currentDisplayedId = '';
    }

    setStreamPicker({
      open: true,
      tabId: tab.active_stream_id,
      currentStreamId: currentDisplayedId || '',
      tabName: tab.stream_name || tab.stream_tab_name || 'Active Stream',
    });
    setPickerSearch('');
  };

  const closeStreamPicker = () => {
    setStreamPicker({ open: false, tabId: null, currentStreamId: '', tabName: '' });
    setPickerSearch('');
  };

  const filteredStreams = allStreams.filter((stream) => {
    const query = pickerSearch.trim().toLowerCase();
    if (!query) return true;
    return [stream.stream_name, stream.stream_type, stream.stream_tab_name, stream.stream_video_link, stream.video_youtube_link]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query));
  });

  const pickStream = async (streamId) => {
    if (!streamPicker.tabId) return;
    await handleUpdateDisplayedStream(streamPicker.tabId, streamId);
    closeStreamPicker();
  };

  if (loading) {
    return <div className="alert alert-info">Loading active streams...</div>;
  }

  return (
    <div className="stream-management-container">
      <h3 className="mb-4">📺 Active Streams Management</h3>

      {/* Success/Error Messages */}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Active Streams List */}
      <div className="card">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">Active Stream Tabs ({activeTabs.length})</h5>
        </div>
        <div className="card-body">
          {activeTabs.length === 0 ? (
            <div className="alert alert-warning">
              📭 No active stream tabs yet. Add one using the form above!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Tab Name</th>
                    <th>Stream Selection</th>
                    <th>Currently Displayed</th>
                    <th>Status</th>
                    <th>Display Order</th>
                    <th>Title</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeTabs.map(tab => (
                    <tr key={tab.active_stream_id}>
                      <td>
                        <strong>{tab.stream_name}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {(() => {
                            let currentDisplayedId = '';
                            let currentDisplayedLabel = '-- Select Stream --';
                            try {
                              const v = tab.is_currently_displayed;
                              if (typeof v === 'string') {
                                const t = v.trim();
                                if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
                                  const obj = JSON.parse(t);
                                  currentDisplayedId = obj.stream_id || '';
                                  currentDisplayedLabel = obj.stream_name || currentDisplayedLabel;
                                }
                              } else if (typeof v === 'object' && v !== null) {
                                currentDisplayedId = v.stream_id || '';
                                currentDisplayedLabel = v.stream_name || currentDisplayedLabel;
                              }
                            } catch (e) {
                              currentDisplayedId = '';
                            }

                            return (
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openStreamPicker(tab)}
                                >
                                  {currentDisplayedId ? 'Change Stream' : 'Select Stream'}
                                </button>
                                <span className="small text-muted" style={{ minWidth: '140px' }}>{currentDisplayedLabel}</span>
                              </div>
                            );
                          })()}
                          <button
                            className="btn btn-sm btn-outline-secondary px-2 py-1"
                            title="Sync latest stream data"
                            onClick={() => {
                              const streamId = allStreams.find(s => s.stream_name === tab.stream_name)?.id;
                              if (streamId) {
                                handleUpdateDisplayedStream(tab.active_stream_id, streamId);
                              }
                            }}
                          >
                            🔄
                          </button>
                        </div>
                      </td>
                      <td>
                        {tab.stream_name && tab.is_currently_displayed ? (
                          (() => {
                            // Handle different shapes: JSON string (structured), raw string, or null
                            let displayedValue = tab.is_currently_displayed;
                            let displayedField = null;

                            if (typeof displayedValue === 'string') {
                              const trimmed = displayedValue.trim();
                              if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                                try {
                                  const obj = JSON.parse(trimmed);
                                  displayedField = obj.currently_displayed_field || null;
                                  if (displayedField && obj[displayedField]) {
                                    displayedValue = obj[displayedField];
                                  } else {
                                    // fallback: show stream name or generic label
                                    displayedValue = obj.stream_name || JSON.stringify(obj);
                                  }
                                } catch (e) {
                                  // not JSON, keep as string
                                }
                              }
                            } else if (typeof displayedValue === 'object' && displayedValue !== null) {
                              displayedField = displayedValue.currently_displayed_field || null;
                              if (displayedField && displayedValue[displayedField]) {
                                displayedValue = displayedValue[displayedField];
                              } else {
                                displayedValue = displayedValue.stream_name || JSON.stringify(displayedValue);
                              }
                            }

                            // Determine icon/label based on field name if available
                            const fieldKey = displayedField;

                            const rowId = tab.active_stream_id;
                            const isExpanded = !!expandedRows[rowId];

                            // If the displayedValue is a comma-separated list, show only a few items
                            let previewText = displayedValue;
                            let isTruncatable = false;
                            if (typeof displayedValue === 'string' && displayedValue.includes(',')) {
                              const parts = displayedValue.split(',').map(p => p.trim()).filter(Boolean);
                              if (parts.length > 3) {
                                isTruncatable = true;
                                previewText = isExpanded ? parts.join(', ') : parts.slice(0, 3).join(', ') + ', ...';
                              }
                            } else if (typeof displayedValue === 'string' && displayedValue.length > 120) {
                              isTruncatable = true;
                              previewText = isExpanded ? displayedValue : displayedValue.slice(0, 120) + '...';
                            }

                            return (
                              <div style={{ fontSize: '0.85em' }}>
                                <strong style={{ color: '#007bff' }}>{tab.stream_name}</strong>
                                <br />
                                <span className="badge bg-info" style={{ marginTop: '4px', maxWidth: '100%', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewText}</span>
                                {isTruncatable && (
                                  <button
                                    className="btn btn-sm btn-link"
                                    style={{ padding: '0 6px', marginLeft: 6 }}
                                    onClick={() => setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }))}
                                    aria-expanded={isExpanded}
                                  >
                                    {isExpanded ? 'Show less' : 'Show more'}
                                  </button>
                                )}
                                {fieldKey === 'video_youtube_link' && (
                                  <div style={{ marginTop: '4px', color: '#666' }}><small>🎬 YouTube</small></div>
                                )}
                                {fieldKey === 'stream_video_link' && (
                                  <div style={{ marginTop: '4px', color: '#666' }}><small>🎥 Video Link</small></div>
                                )}
                                {fieldKey === 'stream_video_path' && (
                                  <div style={{ marginTop: '4px', color: '#666' }}><small>📁 Video File</small></div>
                                )}
                                {fieldKey === 'stream_live_user_id' && (
                                  <div style={{ marginTop: '4px', color: '#666' }}><small>🔴 Live Stream</small></div>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-muted">-- Not Set --</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${tab.is_active === 1 ? 'bg-success' : 'bg-danger'}`}>
                          {tab.is_active === 1 ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-primary">{tab.display_order || 0}</span>
                      </td>
                      <td>
                        {editingTabId === tab.active_stream_id ? (
                          <div className="input-group input-group-sm">
                            <input
                              type="text"
                              className="form-control"
                              defaultValue={tab.title || tab.stream_tab_name || ''}
                              id={`title-${tab.active_stream_id}`}
                              autoFocus
                            />
                            <button
                              className="btn btn-success btn-sm"
                              type="button"
                              onClick={() => {
                                const newTitle = document.getElementById(`title-${tab.active_stream_id}`).value;
                                if (newTitle.trim()) {
                                  handleUpdateTabTitle(tab.active_stream_id, newTitle);
                                } else {
                                  setError('Title cannot be empty');
                                }
                              }}
                            >
                              ✓
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              type="button"
                              onClick={() => setEditingTabId(null)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex justify-content-between align-items-center">
                            <span>{tab.title || tab.stream_tab_name || '---'}</span>
                            <button
                              className="btn btn-sm btn-outline-secondary ms-2"
                              onClick={() => setEditingTabId(tab.active_stream_id)}
                              title="Edit title"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          className={`btn btn-sm ${tab.is_active === 1 ? 'btn-warning' : 'btn-info'} me-2`}
                          onClick={() => handleToggleActive(tab.active_stream_id)}
                          title={tab.is_active === 1 ? 'Deactivate' : 'Activate'}
                        >
                          {tab.is_active === 1 ? '🔴 Deactivate' : '🟢 Activate'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteActiveStream(tab.active_stream_id)}
                          title="Remove from active tabs"
                        >
                          🗑️ Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {streamPicker.open && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={closeStreamPicker}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Stream for {streamPicker.tabName}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeStreamPicker}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search streams..."
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                  />
                </div>
                <div className="list-group">
                  {filteredStreams.length === 0 ? (
                    <div className="text-muted">No streams match your search.</div>
                  ) : (
                    filteredStreams.map((stream) => (
                      <button
                        key={stream.id}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start ${String(stream.id) === String(streamPicker.currentStreamId) ? 'active' : ''}`}
                        onClick={() => pickStream(stream.id)}
                      >
                        <div>
                          <div>{stream.stream_name}</div>
                          <small className="text-muted">{stream.stream_type || 'Unknown type'}</small>
                        </div>
                        {String(stream.id) === String(streamPicker.currentStreamId) && (
                          <span className="badge bg-light text-dark">Current</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeStreamPicker}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveStreamsManagement;
